import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Paper, SvgIcon, Typography } from "@mui/material";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import SellerMessageBubble from "../components/SellerMessageBubble";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";

function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function CalendarIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 7h14v12H5V7Zm3-3v6m8-6v6M5 11h14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </SvgIcon>
  );
}
function ActivityIcon({ type, ...props }) {
  const paths = {
    view: "M4 12s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5Zm8 2.5A2.5 2.5 0 1 0 12 9a2.5 2.5 0 0 0 0 5.5Z",
    nda: "M7 4h7l3 3v13H7V4Zm7 0v4h4M9 13l2 2 4-4",
    question: "M5 5h14v10H9l-4 4V5Zm6 7h2m-2-4h2",
    offer: "M4 17 9 12l3 3 7-8M14 7h5v5",
  };
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d={paths[type] || paths.view} fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

const KPI_DATA = [
  { label: "Valuation midpoint", value: "2.40M", sub: "Enterprise value", accent: true },
  { label: "Buyers viewing", value: "14", sub: "3 new this week" },
  { label: "NDAs signed", value: "6", sub: "2 pending review" },
  { label: "Offers received", value: "2", sub: "1 above ask" },
];

const TODOS = [
  { title: "Review buyer question", due: "Today" },
  { title: "Approve NDA request", due: "Tomorrow" },
  { title: "Prepare management meeting", due: "30 Aug" },
];

const ACTIVITIES = [
  { type: "view", label: "Mekong Capital viewed your CIM", time: "12 min ago" },
  { type: "nda", label: "VinaPrivate Equity signed NDA", time: "2 hr ago" },
  { type: "question", label: "Anonymous Buyer #41 asked a question", time: "Yesterday" },
  { type: "offer", label: "Mekong Capital submitted indicative offer", time: "Yesterday" },
];

function sameDate(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function startOfDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}
function formatSelectedDate(date) {
  if (!date) return "Select a date";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
}
function monthLabel(date) {
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(date);
}
function buildMonthCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: 42 }, (_, index) => {
    const day = index - offset + 1;
    if (day < 1 || day > days) return null;
    return new Date(year, month, day);
  });
}
function greetingForHour(hour) {
  if (hour < 5) return "Good midnight";
  if (hour < 11) return "Good morning";
  if (hour < 13) return "Good noon";
  if (hour < 18) return "Good afternoon";
  return "Good night";
}

function CalendarCard({ metrics, height }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const cells = useMemo(() => buildMonthCells(month), [month]);
  const local = useMemo(() => {
    const width = metrics.contentWidth;
    return {
      cell: Math.round(d3.scaleLinear().domain([700, 950]).range([27, 33]).clamp(true)(width)),
      innerGap: Math.round(d3.scaleLinear().domain([700, 950]).range([8, 12]).clamp(true)(width)),
      daySize: Math.round(d3.scaleLinear().domain([700, 950]).range([8, 10]).clamp(true)(width)),
      navButton: Math.round(d3.scaleLinear().domain([700, 950]).range([30, 34]).clamp(true)(width)),
    };
  }, [metrics.contentWidth]);
  const shiftMonth = (delta) => setMonth((value) => new Date(value.getFullYear(), value.getMonth() + delta, 1));
  return (
    <Paper elevation={0} sx={{ height: `${height}px`, border: "1px solid rgba(13,25,65,0.11)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", p: `${metrics.cardPadding}px`, boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: ARAGON_FONTS.secondary, fontWeight: 700, color: ARAGON_COLORS.burgundy }}>Schedule meeting</Typography>
          <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>{monthLabel(month)}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "6px" }}>
          {[[-1, "‹"], [1, "›"]].map(([delta, label]) => (
            <Button key={delta} onClick={() => shiftMonth(delta)} sx={{ minWidth: `${local.navButton}px`, width: `${local.navButton}px`, height: `${local.navButton}px`, p: 0, border: "1px solid rgba(13,25,65,0.12)", color: ARAGON_COLORS.navy, fontFamily: ARAGON_FONTS.secondary, fontSize: "18px", lineHeight: 1, borderRadius: "7px" }}>{label}</Button>
          ))}
        </Box>
      </Box>
      <Box sx={{ mt: `${local.innerGap}px`, display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "3px", flexShrink: 0 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <Typography key={day} sx={{ textAlign: "center", fontSize: `${local.daySize - 1}px`, color: "rgba(13,25,65,0.48)", fontFamily: ARAGON_FONTS.secondary, fontWeight: 700, letterSpacing: "0.05em" }}>{day}</Typography>)}
        {cells.map((date, index) => {
          if (!date) return <Box key={`empty-${index}`} sx={{ height: `${local.cell}px` }} />;
          const past = startOfDay(date) < today;
          const active = sameDate(date, selected);
          const current = sameDate(date, today);
          return (
            <Box
              key={date.toISOString()}
              role="button"
              tabIndex={past ? -1 : 0}
              onClick={() => !past && setSelected(date)}
              onKeyDown={(event) => {
                if (!past && (event.key === "Enter" || event.key === " ")) setSelected(date);
              }}
              sx={{
                height: `${local.cell}px`,
                display: "grid",
                placeItems: "center",
                borderRadius: "6px",
                fontSize: `${local.daySize}px`,
                cursor: past ? "default" : "pointer",
                color: past ? "rgba(13,25,65,0.24)" : active ? "#fff" : ARAGON_COLORS.navy,
                bgcolor: active ? ARAGON_COLORS.navy : "transparent",
                border: current && !active ? `1px solid ${ARAGON_COLORS.burgundy}` : "1px solid transparent",
                "&:hover": past ? {} : { bgcolor: active ? ARAGON_COLORS.navy : "rgba(224,217,199,0.52)" },
              }}
            >
              {date.getDate()}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: "auto", pt: `${local.innerGap + 2}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", borderTop: "1px solid rgba(13,25,65,0.08)", flexShrink: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: `${Math.max(8, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: ARAGON_FONTS.secondary, color: ARAGON_COLORS.steel }}>Selected date</Typography>
          <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{formatSelectedDate(selected)}</Typography>
          {scheduled && <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.burgundy }}>Scheduled for {formatSelectedDate(scheduled)}</Typography>}
        </Box>
        <Button
          disabled={!selected}
          onClick={() => selected && setScheduled(selected)}
          startIcon={<CalendarIcon sx={{ fontSize: "14px !important" }} />}
          sx={{ px: "15px", height: "34px", bgcolor: ARAGON_COLORS.navy, color: "#fff", borderRadius: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, whiteSpace: "nowrap", "&:hover": { bgcolor: "#142455" }, "&.Mui-disabled": { bgcolor: "rgba(13,25,65,0.08)", color: "rgba(13,25,65,0.35)" } }}
        >
          {selected ? `Schedule · ${formatSelectedDate(selected).replace(/\s\d{4}$/, "")}` : "Submit schedule"}
        </Button>
      </Box>
    </Paper>
  );
}

function TodoCard({ metrics, height }) {
  return (
    <Paper elevation={0} sx={{ height: `${height}px`, border: "1px solid rgba(13,25,65,0.11)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", p: `${metrics.cardPadding}px`, boxSizing: "border-box", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: ARAGON_FONTS.secondary, fontWeight: 700, color: ARAGON_COLORS.burgundy }}>To-dos</Typography>
        <Typography sx={{ fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>{TODOS.length}</Typography>
      </Box>
      <Box sx={{ mt: "14px", display: "flex", flexDirection: "column" }}>
        {TODOS.map((todo, index) => (
          <Box key={todo.title} sx={{ py: "13px", borderTop: index === 0 ? "none" : "1px solid rgba(13,25,65,0.08)" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "13px 1fr", gap: "8px", alignItems: "start" }}>
              <Box sx={{ mt: "3px", width: "10px", height: "10px", borderRadius: "50%", border: "1px solid rgba(13,25,65,0.30)" }} />
              <Box>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 600, lineHeight: 1.35, color: ARAGON_COLORS.navy }}>{todo.title}</Typography>
                <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: todo.due === "Today" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel }}>{todo.due}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function SellerDashboardContent(metrics) {
  const navigate = useNavigate();
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const greeting = greetingForHour(clock.getHours());
  const sellerName = "Minh";
  const actionHeight = Math.round(d3.scaleLinear().domain([620, 1080]).range([345, 415]).clamp(true)(metrics.viewport.height));
  const kpiHeight = Math.round(d3.scaleLinear().domain([620, 1080]).range([94, 116]).clamp(true)(metrics.viewport.height));
  const kpiValueSize = Math.round(d3.scaleLinear().domain([700, 950]).range([22, 28]).clamp(true)(metrics.contentWidth));
  const kpiSubGap = Math.round(d3.scaleLinear().domain([700, 950]).range([7, 9]).clamp(true)(metrics.contentWidth));
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "22px" }}>
        <Box>
          <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>{greeting}, {sellerName}</Typography>
          <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>Two new buyer actions need your review.</Typography>
        </Box>
        <Button onClick={() => navigate("/seller/deal-room")} endIcon={<ArrowIcon sx={{ fontSize: "14px !important" }} />} sx={{ mt: "1px", height: "34px", px: "15px", bgcolor: ARAGON_COLORS.navy, color: "#fff", borderRadius: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, "&:hover": { bgcolor: "#142455" } }}>Open deal room</Button>
      </Box>
      <Box sx={{ mt: `${metrics.cardGap + 12}px`, display: "grid", gridTemplateColumns: metrics.compact ? "repeat(2, minmax(0,1fr))" : "repeat(4, minmax(0,1fr))", gap: `${metrics.cardGap}px` }}>
        {KPI_DATA.map((item) => (
          <Paper key={item.label} elevation={0} sx={{ minHeight: `${kpiHeight}px`, p: `${metrics.cardPadding - 2}px`, border: "1px solid rgba(13,25,65,0.10)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", boxSizing: "border-box", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.13em", textTransform: "uppercase", fontFamily: ARAGON_FONTS.secondary, color: ARAGON_COLORS.burgundy, fontWeight: 700 }}>{item.label}</Typography>
            <Typography sx={{ mt: "6px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${item.accent ? kpiValueSize + 1 : kpiValueSize}px`, lineHeight: 1, color: ARAGON_COLORS.navy }}>{item.value}</Typography>
            <Typography sx={{ mt: `${kpiSubGap}px`, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, lineHeight: 1.3, color: item.accent ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel }}>{item.sub}</Typography>
          </Paper>
        ))}
      </Box>
      <Box sx={{ mt: `${metrics.cardGap}px`, display: "grid", gridTemplateColumns: metrics.tablet ? "1fr" : "minmax(0, 2.3fr) minmax(220px, 1fr)", gap: `${metrics.cardGap}px`, alignItems: "stretch" }}>
        <CalendarCard metrics={metrics} height={actionHeight} />
        <TodoCard metrics={metrics} height={actionHeight} />
      </Box>
      <Paper elevation={0} sx={{ mt: `${metrics.cardGap + 3}px`, border: "1px solid rgba(13,25,65,0.11)", borderRadius: `${metrics.cardRadius}px`, bgcolor: "#FFFEFC", overflow: "hidden" }}>
        <Box sx={{ px: `${metrics.cardPadding}px`, py: `${metrics.cardPadding - 2}px`, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: `${metrics.eyebrowSize}px`, letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: ARAGON_FONTS.secondary, fontWeight: 700, color: ARAGON_COLORS.burgundy }}>Buyer activity</Typography>
            <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>Recent signals</Typography>
          </Box>
          <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, color: ARAGON_COLORS.navy }}>View all →</Typography>
        </Box>
        <Divider sx={{ borderColor: "rgba(13,25,65,0.08)" }} />
        {ACTIVITIES.map((activity, index) => (
          <Box key={activity.label} sx={{ minHeight: "58px", px: `${metrics.cardPadding}px`, display: "grid", gridTemplateColumns: "32px minmax(0,1fr) auto", alignItems: "center", gap: "12px", borderTop: index === 0 ? "none" : "1px solid rgba(13,25,65,0.07)" }}>
            <Box sx={{ width: "28px", height: "28px", borderRadius: "50%", bgcolor: "rgba(13,25,65,0.045)", display: "grid", placeItems: "center", color: ARAGON_COLORS.navy }}><ActivityIcon type={activity.type} sx={{ fontSize: "15px" }} /></Box>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(11, metrics.bodySize)}px`, lineHeight: 1.35, color: ARAGON_COLORS.navy }}>{activity.label}</Typography>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, whiteSpace: "nowrap", color: ARAGON_COLORS.steel }}>{activity.time}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

function useDashboardLayoutMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([700, 950]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([18, 26]).clamp(true)(height)),
    };
  }, [shared]);
}

export default function SellerDashboard() {
  const metrics = useDashboardLayoutMetrics();
  return (
    <LayoutMapping
      metrics={metrics}
      sidebar={<WorkspaceSidebar metrics={metrics} />}
      floatingUtility={<SellerMessageBubble metrics={metrics} />}
    >
      <SellerDashboardContent {...metrics} />
    </LayoutMapping>
  );
}
